import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class FlagService {
  
  private readonly FLAG_BASE_URL = 'https://flagcdn.com';
  private readonly FALLBACK_FLAG = '🏳️';
  
  // Mapping of language codes to country codes for flags
  private readonly LANGUAGE_TO_COUNTRY: { [key: string]: string } = {
    'en': 'gb',    // English -> United Kingdom
    'fr': 'fr',    // French -> France  
    'es': 'es',    // Spanish -> Spain
    'pt': 'pt',    // Portuguese -> Portugal
    'de': 'de',    // German -> Germany
    'it': 'it',    // Italian -> Italy
    'zh': 'cn',    // Chinese -> China
    'ja': 'jp',    // Japanese -> Japan
    'ko': 'kr',    // Korean -> South Korea
    'ru': 'ru',    // Russian -> Russia
    'ar': 'sa',    // Arabic -> Saudi Arabia
    'hi': 'in',    // Hindi -> India
    'nl': 'nl',    // Dutch -> Netherlands
    'sv': 'se',    // Swedish -> Sweden
    'no': 'no',    // Norwegian -> Norway
    'da': 'dk',    // Danish -> Denmark
    'fi': 'fi',    // Finnish -> Finland
    'pl': 'pl',    // Polish -> Poland
    'tr': 'tr',    // Turkish -> Turkey
    'el': 'gr',    // Greek -> Greece
    'cs': 'cz',    // Czech -> Czech Republic
    'hu': 'hu',    // Hungarian -> Hungary
    'ro': 'ro',    // Romanian -> Romania
    'bg': 'bg',    // Bulgarian -> Bulgaria
    'hr': 'hr',    // Croatian -> Croatia
    'sk': 'sk',    // Slovak -> Slovakia
    'sl': 'si',    // Slovenian -> Slovenia
    'et': 'ee',    // Estonian -> Estonia
    'lv': 'lv',    // Latvian -> Latvia
    'lt': 'lt',    // Lithuanian -> Lithuania
    'mt': 'mt',    // Maltese -> Malta
    'cy': 'gb',    // Welsh -> United Kingdom
    'ga': 'ie',    // Irish -> Ireland
    'gd': 'gb',    // Scottish Gaelic -> United Kingdom
    'eu': 'es',    // Basque -> Spain
    'ca': 'es',    // Catalan -> Spain
    'gl': 'es',    // Galician -> Spain
    'is': 'is',    // Icelandic -> Iceland
    'mk': 'mk',    // Macedonian -> North Macedonia
    'sr': 'rs',    // Serbian -> Serbia
    'bs': 'ba',    // Bosnian -> Bosnia and Herzegovina
    'me': 'me',    // Montenegrin -> Montenegro
    'sq': 'al',    // Albanian -> Albania
    'be': 'by',    // Belarusian -> Belarus
    'uk': 'ua',    // Ukrainian -> Ukraine
    'hy': 'am',    // Armenian -> Armenia
    'ka': 'ge',    // Georgian -> Georgia
    'az': 'az',    // Azerbaijani -> Azerbaijan
    'kk': 'kz',    // Kazakh -> Kazakhstan
    'ky': 'kg',    // Kyrgyz -> Kyrgyzstan
    'uz': 'uz',    // Uzbek -> Uzbekistan
    'tg': 'tj',    // Tajik -> Tajikistan
    'tm': 'tm',    // Turkmen -> Turkmenistan
    'mn': 'mn',    // Mongolian -> Mongolia
    'mn-CN': 'cn', // Mongolian (China) -> China
    'mn-MN': 'mn', // Mongolian (Mongolia) -> Mongolia
    'bo': 'cn',    // Tibetan -> China
    'dz': 'bt',    // Dzongkha -> Bhutan
    'ne': 'np',    // Nepali -> Nepal
    'si': 'lk',    // Sinhala -> Sri Lanka
    'my': 'mm',    // Burmese -> Myanmar
    'km': 'kh',    // Khmer -> Cambodia
    'lo': 'la',    // Lao -> Laos
    'th': 'th',    // Thai -> Thailand
    'vi': 'vn',    // Vietnamese -> Vietnam
    'ms': 'my',    // Malay -> Malaysia
    'id': 'id',    // Indonesian -> Indonesia
    'tl': 'ph',    // Filipino -> Philippines
    'jv': 'id',    // Javanese -> Indonesia
    'su': 'id',    // Sundanese -> Indonesia
    'mg': 'mg',    // Malagasy -> Madagascar
    'ny': 'mw',    // Chewa -> Malawi
    'sn': 'zw',    // Shona -> Zimbabwe
    'yo': 'ng',    // Yoruba -> Nigeria
    'ig': 'ng',    // Igbo -> Nigeria
    'ha': 'ne',    // Hausa -> Niger
    'sw': 'ke',    // Swahili -> Kenya
    'zu': 'za',    // Zulu -> South Africa
    'af': 'za',    // Afrikaans -> South Africa
    'xh': 'za',    // Xhosa -> South Africa
    'st': 'za',    // Southern Sotho -> South Africa
    'tn': 'za',    // Tswana -> South Africa
    'ts': 'za',    // Tsonga -> South Africa
    'ss': 'za',    // Swazi -> South Africa
    've': 'za',    // Venda -> South Africa
    'nr': 'za',    // Southern Ndebele -> South Africa
    'om': 'et',    // Oromo -> Ethiopia
    'ti': 'et',    // Tigrinya -> Ethiopia
    'am': 'et',    // Amharic -> Ethiopia
    'so': 'so',    // Somali -> Somalia
    'rw': 'rw',    // Kinyarwanda -> Rwanda
    'rn': 'bi',    // Kirundi -> Burundi
  };

  constructor() { }

  /**
   * Get flag image URL for a language code
   * @param langCode Language code (e.g., 'en', 'fr', 'es')
   * @returns Flag image URL or fallback emoji
   */
  getFlagUrl(langCode: string): string {
    if (!langCode || typeof langCode !== 'string') return this.FALLBACK_FLAG;
    
    const countryCode = this.LANGUAGE_TO_COUNTRY[langCode.toLowerCase()];
    if (!countryCode) return this.FALLBACK_FLAG;
    
    return `${this.FLAG_BASE_URL}/w40/${countryCode.toLowerCase()}.png`;
  }

  /**
   * Get flag image URL with custom size
   * @param langCode Language code
   * @param size Size in pixels (e.g., 20, 40, 80)
   * @returns Flag image URL
   */
  getFlagUrlWithSize(langCode: string, size: number): string {
    if (!langCode || typeof langCode !== 'string') return this.FALLBACK_FLAG;
    
    const countryCode = this.LANGUAGE_TO_COUNTRY[langCode.toLowerCase()];
    if (!countryCode) return this.FALLBACK_FLAG;
    
    // Calculate height maintaining 4:3 aspect ratio
    const height = Math.round(size * 0.75);
    return `${this.FLAG_BASE_URL}/${size}x${height}/${countryCode.toLowerCase()}.png`;
  }

  /**
   * Get fallback emoji for language
   * @param langCode Language code
   * @returns Fallback emoji
   */
  getFallbackFlag(langCode: string): string {
    if (!langCode || typeof langCode !== 'string') return this.FALLBACK_FLAG;
    
    // Simple fallback using language name first letter
    const fallbacks: { [key: string]: string } = {
      'en': 'en', 'fr': '🇫🇷', 'es': '🇪🇸', 'pt': '🇵🇹', 'de': '🇩🇪', 'it': '🇮🇹',
      'zh': '🇨🇳', 'ja': '🇯🇵', 'ko': '🇰🇷', 'ru': '🇷🇺', 'ar': '🇸🇦', 'hi': '🇮🇳'
    };
    
    return fallbacks[langCode.toLowerCase()] || this.FALLBACK_FLAG;
  }

  /**
   * Check if flag is available for language
   * @param langCode Language code
   * @returns Boolean indicating flag availability
   */
  hasFlag(langCode: string): boolean {
    if (!langCode || typeof langCode !== 'string') return false;
    return !!this.LANGUAGE_TO_COUNTRY[langCode.toLowerCase()];
  }

  /**
   * Get SVG flag URL for high quality display
   * @param langCode Language code
   * @returns SVG flag URL or fallback emoji
   */
  getFlagSvgUrl(langCode: string): string {
    if (!langCode || typeof langCode !== 'string') return this.FALLBACK_FLAG;
    
    const countryCode = this.LANGUAGE_TO_COUNTRY[langCode.toLowerCase()];
    if (!countryCode) return this.FALLBACK_FLAG;
    
    return `${this.FLAG_BASE_URL}/${countryCode.toLowerCase()}.svg`;
  }
}
